// login request here
import toast from 'react-hot-toast';
import progressMagicJwtToken from '../../config/constants';
import store from '../../redux/store';
import { IBrandConfigurations } from '../../redux/coach/coachSlice';
import { UserType } from '../../components/User/User';
import { IdentityProviderEnum } from '../../types/coach';
import { toastStyles } from '../../utils/utils';
import i18n from '../../i18n';
import { logout } from '../../redux/GlobalAction';

export interface ILoginResponse {
  ok: boolean;
  status: number;
  json: Function;
  username?: string;
  accessToken?: {
    jwtToken: string;
  };
  refreshToken?: {
    token: string;
  };
  userId?: string;
  userType?: UserType;
  message?: string;
  brandConfigurations?: IBrandConfigurations | null;
}

export interface IRedirectUrlReponse {
  ok: boolean;
  status: number;
  json: Function;
  redirectUrl: string;
}

export interface IChangeUserPasswordParams {
  username: string;
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface IAuthResponse {
  json: Function;
  statusCode: number;
  message: string;
}

export async function coachLogin(username: string, password: string): Promise<ILoginResponse> {
  try {
    const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        toast.error(i18n.t('auth.toastMessage.loginUnsuccessful'), toastStyles.error);
        throw new Error('Incorrect username or password.');
      } else {
        toast.error('An error occurred during login. Please try again later.', toastStyles.error);
        throw new Error('Login failed.');
      }
    }
    return response;
  } catch (error) {
    throw new Error('Error while logging in');
  }
}

export const isTokenValid = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE}/auth/sessionstatus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Token validation failed');
    }

    const data = await response.json();
    return data.isValid;
  } catch (error) {
    return false;
  }
};

export const refreshAccessToken = async (): Promise<{
  accessToken: string;
}> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE}/auth/refreshSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ refreshToken: '' }),
    });

    if (!response.ok) {
      // If refresh fails, log out the user
      toast.error('Session expired. Please log in again.', toastStyles.error);
      store.dispatch(logout());
      throw new Error('Session expired. Please log in again.');
    }

    const data = await response.json();
    const { accessToken } = data;

    // store jwtToken in local storage
    localStorage.setItem(progressMagicJwtToken, accessToken);

    return data;
  } catch (error: unknown) {
    // If refresh fails, log out the user
    toast.error('Session expired. Please log in again.', toastStyles.error);
    store.dispatch(logout());
    if (error instanceof Error) {
      throw new Error(error.message);
    }

    throw error;
  }
};

export async function changeUserPassword({
  username,
  oldPassword,
  newPassword,
  confirmPassword,
}: IChangeUserPasswordParams): Promise<Response> {
  const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE}/auth/changeUserPassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      oldPassword,
      newPassword,
      confirmPassword,
    }),
  });
  return response;
}

export async function sendForgetPasswordEmail(username: string): Promise<Response> {
  const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE}/auth/forgotPassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: username,
    }),
  });

  return response;
}

export async function resetPassword(
  email: string,
  verificationCode: string,
  newPassword: string
): Promise<Response> {
  const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE}/auth/resetPassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      verificationCode,
      newPassword,
    }),
  });

  return response;
}

export async function changeCoachOnboardingPassword(
  username: string,
  newPassword: string,
  approvalId: string
): Promise<Response> {
  const response = await fetch(
    `${process.env.REACT_APP_AUTH_SERVICE}/auth/changeCoachOnboardingPassword`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        newPassword,
        approvalId,
      }),
    }
  );

  return response;
}

export async function redirectUrl(identityProvider: IdentityProviderEnum): Promise<ILoginResponse> {
  const queryParams = new URLSearchParams({ identityProvider });
  const response = await fetch(
    `${process.env.REACT_APP_AUTH_SERVICE}/auth/redirect-url?${queryParams}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
  return response;
}

export async function socialSignIn(
  code: string,
  currentLocation?: string,
  brandTag?: string
): Promise<ILoginResponse> {
  const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE}/auth/social-sign-in`, {
    method: 'POST',
    body: JSON.stringify({ code, currentLocation, brandTag }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response;
}

export { UserType };
